import React, { useEffect, useState } from "react";
import {
  Modal, Form, Button, Dropdown, Input, InputNumber,
  Switch, Tag, message, Select,
} from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  MedicineBoxOutlined, PoweroffOutlined, MoreOutlined,
} from "@ant-design/icons";
import axios from "axios";
import AppTable from "../../common/AppTable";
import AppInput from "../../common/FormInputs/AppInput";

const API = axios.create({ baseURL: process.env.REACT_APP_API_URL });
API.interceptors.request.use((c) => {
  const t = localStorage.getItem("token");
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

const AGE_GROUPS = [
  "At Birth", "6 Weeks", "10 Weeks", "14 Weeks",
  "6 Months", "9 Months", "12 Months", "15 Months",
  "16-18 Months", "18-19 Months", "2 Years",
  "4-6 Years", "10-12 Years",
];

const GROUP_COLORS = {
  "At Birth":      "#EF5350",
  "6 Weeks":       "#FF7043",
  "10 Weeks":      "#FFA726",
  "14 Weeks":      "#FFCA28",
  "6 Months":      "#66BB6A",
  "9 Months":      "#26C6DA",
  "12 Months":     "#42A5F5",
  "15 Months":     "#5C6BC0",
  "16-18 Months":  "#7E57C2",
  "18-19 Months":  "#AB47BC",
  "2 Years":       "#26A69A",
  "4-6 Years":     "#8D6E63",
  "10-12 Years":   "#78909C",
};

const ACCENT = "#534ab7";

export default function VaccineSchedule() {
  const [items,   setItems]   = useState([]);
  const [filter,  setFilter]  = useState("All");
  const [search,  setSearch]  = useState("");
  const [open,    setOpen]    = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const fetchAll = async () => {
    try {
      const res = await API.get("/api/vaccine-schedule-templates/admin/all");
      setItems((res.data.data || []).slice().sort((a, b) => b.id - a.id));
    } catch {
      message.error("Failed to load vaccine schedule");
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ active: true, mandatory: true, sortOrder: items.length, daysFromBirth: 0 });
    setOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    form.setFieldsValue({ ...r });
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      if (editing) {
        await API.put(`/api/vaccine-schedule-templates/${editing.id}`, values);
        message.success("Updated");
      } else {
        await API.post("/api/vaccine-schedule-templates", values);
        message.success("Vaccine added to schedule");
      }
      setOpen(false);
      fetchAll();
    } catch (e) {
      if (e?.errorFields) return;
      message.error("Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (record) => {
    try {
      await API.put(`/api/vaccine-schedule-templates/${record.id}`, { ...record, active: !record.active });
      message.success(record.active ? "Deactivated" : "Activated");
      fetchAll();
    } catch {
      message.error("Failed to update status");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this vaccine from the schedule?")) return;
    try {
      await API.delete(`/api/vaccine-schedule-templates/${id}`);
      message.success("Removed");
      fetchAll();
    } catch {
      message.error("Failed to delete");
    }
  };

  const ageGroups = ["All", ...AGE_GROUPS.filter(g => items.some(i => i.ageGroup === g))];

  const filtered = items.filter((i) => {
    const matchGroup = filter === "All" || i.ageGroup === filter;
    const q = search.toLowerCase();
    const matchQ = !q || i.name?.toLowerCase().includes(q) || i.description?.toLowerCase().includes(q);
    return matchGroup && matchQ;
  });

  // Group for chart view
  const grouped = filtered.reduce((acc, item) => {
    const g = item.ageGroup || "Other";
    if (!acc[g]) acc[g] = [];
    acc[g].push(item);
    return acc;
  }, {});

  const columns = [
    {
      title: "Vaccine",
      dataIndex: "name",
      render: (name, r) => (
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{name}</div>
          <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>{r.description}</div>
          {r.notes && <div style={{ fontSize: 11, color: "#6366F1", marginTop: 2 }}>📝 {r.notes}</div>}
        </div>
      ),
    },
    {
      title: "Age Group",
      dataIndex: "ageGroup",
      width: 130,
      render: (g) => (
        <Tag color={GROUP_COLORS[g] || "#888"}
          style={{ color: "#fff", fontWeight: 700, border: "none" }}>
          {g}
        </Tag>
      ),
    },
    {
      title: "Days",
      dataIndex: "daysFromBirth",
      width: 80,
      align: "center",
      render: (d) => <span style={{ fontSize: 13, color: "#6B7280" }}>+{d}d</span>,
    },
    {
      title: "Type",
      dataIndex: "mandatory",
      width: 100,
      align: "center",
      render: (m) => m
        ? <Tag color="red">Mandatory</Tag>
        : <Tag color="orange">Optional</Tag>,
    },
    {
      title: "Status",
      dataIndex: "active",
      width: 80,
      align: "center",
      render: (a) => <Tag color={a ? "success" : "default"}>{a ? "Active" : "Off"}</Tag>,
    },
    {
      title: "Actions",
      width: 70,
      align: "center",
      render: (_, r) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              { key: "edit", label: "Edit", icon: <EditOutlined />, onClick: () => openEdit(r) },
              {
                key: "toggle",
                label: r.active ? "Deactivate" : "Activate",
                icon: <PoweroffOutlined />,
                onClick: () => handleToggleActive(r),
              },
              { type: "divider" },
              { key: "delete", label: "Remove", icon: <DeleteOutlined />, danger: true, onClick: () => handleDelete(r.id) },
            ],
          }}
        >
          <Button icon={<MoreOutlined />} size="small" />
        </Dropdown>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>
            <MedicineBoxOutlined style={{ color: ACCENT, marginRight: 8 }} />
            Vaccine Schedule
          </h2>
          <p style={{ margin: "4px 0 0", color: "#888", fontSize: 13 }}>
            Master schedule — auto-applied to every new child profile · {items.filter(i => i.active).length} active vaccines
          </p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} style={{ background: ACCENT }} onClick={openAdd}>
          Add Vaccine
        </Button>
      </div>

      {/* Age group filter pills */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
        {ageGroups.map((g) => {
          const color = GROUP_COLORS[g];
          const isActive = filter === g;
          return (
            <div
              key={g}
              onClick={() => setFilter(g)}
              style={{
                cursor: "pointer",
                padding: "5px 14px",
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 600,
                background: isActive ? (color || ACCENT) : "#F3F4F6",
                color: isActive ? "#fff" : "#6B7280",
                transition: "all 0.15s",
                userSelect: "none",
              }}
            >
              {g === "All" ? "All" : g}
              {g !== "All" && (
                <span style={{ marginLeft: 5, opacity: 0.75 }}>
                  {items.filter(i => i.ageGroup === g).length}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Search */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        <Input.Search
          placeholder="Search vaccine name or description…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
        <span style={{ alignSelf: "center", color: "#888", fontSize: 13 }}>
          {filtered.length} vaccine{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Chart view always (search switches to table) */}
      {search ? (
        <AppTable columns={columns} data={filtered} rowKey="id" />
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#9CA3AF" }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>💉</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#374151", marginBottom: 8 }}>
            No vaccines in schedule yet
          </div>
          <div style={{ fontSize: 13 }}>
            Restart the backend server — the schedule seeds automatically on first startup.
          </div>
        </div>
      ) : (
        <div>
          {/* show only the selected group, or all groups if filter === "All" */}
          {(filter === "All" ? AGE_GROUPS : [filter])
            .filter(g => grouped[g]?.length > 0)
            .map((group) => (
              <div key={group} style={{ marginBottom: 20 }}>
                <div style={{
                  display: "flex", alignItems: "center", gap: 10, marginBottom: 8,
                  padding: "8px 14px", borderRadius: 10,
                  background: GROUP_COLORS[group] || "#888",
                }}>
                  <span style={{ fontSize: 16 }}>💉</span>
                  <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>{group}</span>
                  <span style={{
                    marginLeft: "auto", fontSize: 11, background: "rgba(255,255,255,0.25)",
                    color: "#fff", borderRadius: 20, padding: "2px 10px", fontWeight: 600,
                  }}>
                    {grouped[group].length} vaccine{grouped[group].length !== 1 ? "s" : ""}
                  </span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {grouped[group].map((v) => (
                    <div key={v.id} style={{
                      background: "#fff",
                      border: `2px solid ${v.mandatory ? (GROUP_COLORS[group] || "#888") : "#E5E7EB"}`,
                      borderRadius: 12, padding: "10px 14px", minWidth: 160, position: "relative",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                      opacity: v.active ? 1 : 0.5,
                    }}>
                      {!v.mandatory && (
                        <div style={{ position: "absolute", top: 6, right: 8, fontSize: 9, color: "#FFA726", fontWeight: 700 }}>
                          OPTIONAL
                        </div>
                      )}
                      <div style={{ fontWeight: 700, fontSize: 13, color: "#111827", marginBottom: 3 }}>{v.name}</div>
                      <div style={{ fontSize: 11, color: "#6B7280", lineHeight: 1.4 }}>{v.description}</div>
                      {v.notes && <div style={{ fontSize: 10, color: "#6366F1", marginTop: 4 }}>📝 {v.notes}</div>}
                      <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                        <Dropdown
                          trigger={["click"]}
                          menu={{
                            items: [
                              { key: "edit", label: "Edit", icon: <EditOutlined />, onClick: () => openEdit(v) },
                              {
                                key: "toggle",
                                label: v.active ? "Deactivate" : "Activate",
                                icon: <PoweroffOutlined />,
                                onClick: () => handleToggleActive(v),
                              },
                              { type: "divider" },
                              { key: "delete", label: "Remove", icon: <DeleteOutlined />, danger: true, onClick: () => handleDelete(v.id) },
                            ],
                          }}
                        >
                          <Button size="small" icon={<MoreOutlined />} />
                        </Dropdown>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      )}

      {/* Add / Edit Modal */}
      <Modal
        title={editing ? `Edit — ${editing.name}` : "Add Vaccine to Schedule"}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={handleSave}
        confirmLoading={loading}
        width={560}
        okText={editing ? "Save Changes" : "Add to Schedule"}
        okButtonProps={{ style: { background: ACCENT } }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item name="name" label="Vaccine Name" rules={[{ required: true }]}>
            <AppInput placeholder="e.g. DTwP-1" />
          </Form.Item>

          <Form.Item name="description" label="Description" rules={[{ required: true }]}>
            <AppInput placeholder="e.g. Diphtheria, Tetanus & Whooping Cough – 1st dose" />
          </Form.Item>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Form.Item name="ageGroup" label="Age Group" rules={[{ required: true }]}>
              <Select placeholder="Select age group" options={AGE_GROUPS.map(g => ({ value: g, label: g }))} />
            </Form.Item>
            <Form.Item name="daysFromBirth" label="Days from Birth" rules={[{ required: true }]}>
              <InputNumber min={0} max={7300} style={{ width: "100%" }} placeholder="e.g. 42" />
            </Form.Item>
          </div>

          <Form.Item name="notes" label="Administration Notes (optional)">
            <Input.TextArea rows={2} placeholder="e.g. Intramuscular, left thigh; 0.5 ml dose" />
          </Form.Item>

          <Form.Item name="sortOrder" label="Sort Order">
            <InputNumber min={0} max={999} style={{ width: 100 }} />
          </Form.Item>

          <div style={{ display: "flex", gap: 24 }}>
            <Form.Item name="mandatory" label="Mandatory" valuePropName="checked">
              <Switch checkedChildren="Yes" unCheckedChildren="Optional" />
            </Form.Item>
            <Form.Item name="active" label="Active" valuePropName="checked">
              <Switch checkedChildren="Active" unCheckedChildren="Off" />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
